/**
 * The handful of Web API calls Fadila makes, on top of a token *getter*
 * rather than a token.
 *
 * Every request asks spotifyAuth.getFreshAccessToken() first, which is a
 * no-op until expiry is close and a refresh when it is. That is what lets a
 * session outlive the hour an access token is good for - the old implicit
 * flow could only renew through a hidden iframe, and Spotify removed the
 * implicit flow altogether in November 2025.
 *
 * Development Mode since March 2026 (see CLAUDE.md, "Spotify"): only six
 * endpoints are used here, and none of them was removed. The one limit that
 * bites is search - `limit` maxes at 10 now, down from 50.
 */

import QueryIterator from '../../models/queryIterator';
import PagingObject from '../../models/spotify/pagingObject';
import Playlists from '../../models/spotify/playlists';
import { SpotifyAlbum, SpotifyTrackAlbum } from '../../models/spotify/spotifyAlbum';
import SpotifyApiPlaylistTrack, {
  SpotifyApiTrack,
  SpotifyTrackSimplified,
} from '../../models/spotify/spotifyApiTrack';
import SpotifyArtist from '../../models/spotify/spotifyArtist';
import SpotifyPlaylist, { SpotifyPlaylistSimplified } from '../../models/spotify/spotifyPlaylist';
import SpotifySearchResponse, {
  SpotifySearchIterators,
} from '../../models/spotify/spotifySearchResponse';
import UserProfile from '../../models/spotify/userProfile';

// Re-exported: these used to be defined here and are imported from here.
export { clientID, scopes } from './spotifyAuth';

export const apiUrl = 'https://api.spotify.com/v1';

/**
 * The most /search will return per type per page, as of the February 2026
 * Development Mode changes (it was 50). Paging still works through `next`
 * links, so the only visible difference is smaller pages.
 */
export const searchLimitMax = 10;

/** Resolves to the current access token, or undefined when signed out. */
export type TokenSource = () => Promise<string | undefined>;

function createHeaders(accessToken: string) {
  return new Headers({
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  });
}
async function spotifyRequest(
  url: string,
  getToken: TokenSource,
  method = 'GET',
  body: any = undefined,
  skipApiUrl = false,
) {
  const accessToken = await getToken();

  if (accessToken === undefined) {
    throw new Error('Missing access token');
  }

  const res = await fetch(skipApiUrl ? url : apiUrl + url, {
    method,
    headers: createHeaders(accessToken),
    body: body && JSON.stringify(body),
  });

  if (res.status === 204) {
    return;
  }

  if (!res.ok) {
    throw await res.json();
  }

  try {
    const json = await res.json();
    console.debug(json);
    return json;
  } catch {
    return;
  }
}

export async function getUserProfile(getToken: TokenSource): Promise<UserProfile> {
  return await spotifyRequest('/me', getToken);
}
export async function getUserPlaylists(
  getToken: TokenSource,
  limit = 50,
): Promise<QueryIterator<SpotifyPlaylistSimplified>> {
  async function doGetUserPlaylists(): Promise<Playlists> {
    return await spotifyRequest(`/me/playlists?limit=${Math.min(50, limit)}&offset=0`, getToken);
  }

  const response = await doGetUserPlaylists();

  function createGetNextPagedPlaylists(nextLink: string) {
    return async function getNextPagedPlaylists() {
      const response: Playlists = await spotifyRequest(
        nextLink,
        getToken,
        undefined,
        undefined,
        true,
      );

      return {
        results: response.items,
        next: response.next !== null ? createGetNextPagedPlaylists(response.next) : undefined,
        totalResults: response.total,
      };
    };
  }

  return {
    results: response.items,
    next: response.next !== null ? createGetNextPagedPlaylists(response.next) : undefined,
    totalResults: response.total,
  };
}

export async function getPlaylist(
  getToken: TokenSource,
  playlistId: string,
): Promise<QueryIterator<SpotifyApiPlaylistTrack>> {
  const playlist: SpotifyPlaylist = await spotifyRequest(`/playlists/${playlistId}`, getToken);

  const iter = playlist.tracks;

  async function doGet() {
    const pagingObj: PagingObject<SpotifyApiPlaylistTrack> = await spotifyRequest(
      iter.next!,
      getToken,
      undefined,
      undefined,
      true,
    );

    return {
      results: pagingObj.items,
      totalResults: pagingObj.total,
      next: pagingObj.next ? doGet : undefined,
    };
  }

  return {
    results: iter.items,
    totalResults: iter.total,
    next: iter.next ? doGet : undefined,
  };
}
export async function getAlbum(
  getToken: TokenSource,
  albumId: string,
): Promise<QueryIterator<SpotifyApiTrack>> {
  const album: SpotifyAlbum = await spotifyRequest(`/albums/${albumId}`, getToken);
  const { tracks: iter, ...albumProps } = album;

  const trackAlbum: SpotifyTrackAlbum = {
    total_tracks: iter.total,
    ...albumProps,
  };

  // Album tracks come back as simplified track objects; the album is what
  // makes them full ones. (`popularity` used to be copied across here too.
  // Development Mode no longer returns it and nothing ever read it.)
  function toFullTrack(x: SpotifyTrackSimplified): SpotifyApiTrack {
    return {
      ...x,
      album: trackAlbum,
    };
  }

  async function doGet(): Promise<QueryIterator<SpotifyApiTrack>> {
    const pagingObj: PagingObject<SpotifyTrackSimplified> = await spotifyRequest(
      iter.next!,
      getToken,
      undefined,
      undefined,
      true,
    );

    return {
      results: pagingObj.items.map(toFullTrack),
      totalResults: pagingObj.total,
      next: pagingObj.next ? doGet : undefined,
    };
  }

  return {
    results: iter.items.map(toFullTrack),
    totalResults: iter.total,
    next: iter.next ? doGet : undefined,
  };
}

/** Plural of SpotifyType */
export type SpotifyTypes = 'artists' | 'albums' | 'playlists' | 'tracks';
export type SearchResult =
  SpotifyApiTrack | SpotifyTrackAlbum | SpotifyArtist | SpotifyPlaylistSimplified;
/**
 *
 * @param q
 * @param getToken
 * @param type
 * @param limit The limit is applied within each type, not on the total response.
 * For example, if the limit value is 3 and the type is artist,album, the response contains 3 artists and 3 albums.
 * Clamped to searchLimitMax.
 */
export async function search(
  getToken: TokenSource,
  q: string,
  limit = searchLimitMax,
  type = 'album,artist,playlist,track',
): Promise<SpotifySearchIterators> {
  async function doSearch(): Promise<SpotifySearchResponse> {
    return await spotifyRequest(
      `/search?q=${encodeURIComponent(q)}&market=from_token` +
        `&type=${type}&limit=${Math.min(searchLimitMax, limit)}&offset=0`,
      getToken,
    );
  }

  const response = await doSearch();

  function createNext<T>(nextLink: string, type: SpotifyTypes) {
    return async function (): Promise<QueryIterator<T>> {
      const response: SpotifySearchResponse = await spotifyRequest(
        nextLink,
        getToken,
        undefined,
        undefined,
        true,
      );

      if (response[type] !== undefined) {
        const pagingObj: PagingObject<T> = response[type] as any;

        return {
          results: pagingObj.items,
          next: pagingObj.next !== null ? createNext<T>(pagingObj.next, type) : undefined,
          totalResults: pagingObj.total,
        };
      } else {
        // Should never happen
        return {
          results: [],
          next: undefined,
          totalResults: 0,
        };
      }
    };
  }

  return {
    albums: response.albums && {
      results: response.albums.items,
      next: response.albums.next !== null ? createNext(response.albums.next, 'albums') : undefined,
      totalResults: response.albums.total,
    },
    artists: response.artists && {
      results: response.artists.items,
      next:
        response.artists.next !== null ? createNext(response.artists.next, 'artists') : undefined,
      totalResults: response.artists.total,
    },
    playlists: response.playlists && {
      results: response.playlists.items,
      next:
        response.playlists.next !== null
          ? createNext(response.playlists.next, 'playlists')
          : undefined,
      totalResults: response.playlists.total,
    },
    tracks: response.tracks && {
      results: response.tracks.items,
      next: response.tracks.next !== null ? createNext(response.tracks.next, 'tracks') : undefined,
      totalResults: response.tracks.total,
    },
  };
}

export async function playTrack(
  getToken: TokenSource,
  playerId: string,
  trackId: string,
  /** Where to start, for a track resumed from a previous session. */
  positionMs?: number,
) {
  return await spotifyRequest(`/me/player/play?device_id=${playerId}`, getToken, 'PUT', {
    uris: [`spotify:track:${trackId}`],
    ...(positionMs ? { position_ms: Math.round(positionMs) } : {}),
  });
}

/**
 *
 * @param getToken Resolves to a fresh Spotify access token
 * @param wrapper F.x. exception handling
 */
export default function configureSpotifyApi(
  getToken: TokenSource,
  wrapper = <T>(x: () => Promise<T>) => x(),
) {
  return {
    getUserProfile: () => wrapper(() => getUserProfile(getToken)),
    getUserPlaylists: (limit?: number) => wrapper(() => getUserPlaylists(getToken, limit)),
    getPlaylist: (playlistId: string) => wrapper(() => getPlaylist(getToken, playlistId)),
    getAlbum: (albumId: string) => wrapper(() => getAlbum(getToken, albumId)),

    playTrack: (playerId: string, spotifyUri: string, positionMs?: number) =>
      wrapper(() => playTrack(getToken, playerId, spotifyUri, positionMs)),

    search: (q: string, limit?: number, type?: string) =>
      wrapper(() => search(getToken, q, limit, type)),
  };
}
